import React, {
  useCallback, useState, useEffect, useRef,
} from 'react';
import ReactDOM from 'react-dom';

import { useConfigStore, useMapStateStore, useMapInteractionStore } from '../../../stores';
import { createOverlayViewClass, createOverlayElement, getOverlayBounds } from './OverlayViewHelpers';

// eslint-disable-next-line import/no-cycle
import MapOverlay from '../MapOverlay/MapOverlay';

const GoogleMapOverlayViewContainer = () => {
  const [overlayDOMElem, setOverlayDOMElem] = useState(null);

  const isMountedRef = useRef(true);
  const overlayViewRef = useRef(null);
  const setOverlayViewRef = useRef(null);

  // Cleanup mounted ref on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Map interaction store
  const overlayView = useMapInteractionStore((state) => state.overlayView);
  const setOverlayView = useMapInteractionStore((state) => state.setOverlayView);
  setOverlayViewRef.current = setOverlayView; // Keep ref in sync for cleanup

  // Keep ref in sync with state for cleanup
  useEffect(() => {
    overlayViewRef.current = overlayView;
  }, [overlayView]);
  const setOverlayBounds = useMapInteractionStore((state) => state.setOverlayBounds);
  const overlayBounds = useMapInteractionStore((state) => state.overlayBounds);
  const map = useMapInteractionStore((state) => state.map);
  const setOverlayVisible = useMapInteractionStore((state) => state.setOverlayVisible);
  const overlayVisible = useMapInteractionStore((state) => state.overlayVisible);
  const isExternalFocus = useMapInteractionStore((state) => state.isExternalFocus);

  // Zustand stores
  const mapConfig = useConfigStore((state) => state.mapConfig);
  const activeZoom = useMapStateStore((state) => state.activeZoom);
  const activeMapType = useMapStateStore((state) => state.activeMapType);
  const POIVisible = useMapStateStore((state) => state.POIVisible);
  const { tileSource, googleMapsConfig } = mapConfig || {};
  // Use enableOverlay from zooms config for overlay visibility
  const mapZoomToEnableOverlay = googleMapsConfig?.zooms?.enableOverlay;

  // Set overlay bounds.
  // Depends on `map` so it re-runs after Google Maps API is fully loaded,
  // since getOverlayBounds needs google.maps.LatLngBounds.
  useEffect(() => {
    if (tileSource && map) {
      const rawOverlayBounds = getOverlayBounds(tileSource);
      setOverlayBounds(rawOverlayBounds);
    }
  }, [tileSource, map]);

  // Make a div to hold the overlay
  const setOverlayDOMElemCallback = useCallback(() => {
    const el = createOverlayElement();
    setOverlayDOMElem(el);
  }, []);

  useEffect(() => {
    setOverlayDOMElemCallback();
  }, [setOverlayDOMElemCallback]);

  // Create the overlay.
  // Depends on `map` so it re-runs after Google Maps API is fully loaded.
  useEffect(() => {
    if (overlayDOMElem && window?.google?.maps?.OverlayView) {
      const OverlayView = createOverlayViewClass();
      if (OverlayView) {
        const view = new OverlayView({
          content: overlayDOMElem,
          overlayBounds,
        });
        setOverlayView(view);
      }
    }
  }, [
    overlayDOMElem,
    overlayBounds,
    map,
  ]);

  // Tie the overlay to the Google Map and control visibility
  // When overlayVisible is false (e.g., satellite view) or external area
  // locations are focused, detach from map.
  useEffect(() => {
    if (overlayView && map) {
      if (overlayVisible && !isExternalFocus) {
        overlayView.setMap(map);
      } else {
        overlayView.setMap(null);
      }
    }
  }, [overlayView, map, overlayVisible, isExternalFocus]);

  // Cleanup: remove overlay from map ONLY when component unmounts
  useEffect(() => {
    return () => {
      if (overlayViewRef.current) {
        overlayViewRef.current.setMap(null);
        overlayViewRef.current = null;
      }
      // Clear the overlayView reference from the store so next mount starts fresh
      if (setOverlayViewRef.current) {
        setOverlayViewRef.current(null);
      }
    };
  }, []);

  // Toggle between seeing the overlay or the comm pin with mount checks
  useEffect(() => {
    if (!isMountedRef.current) return;

    // Hide overlay while external area locations and their travel paths are
    // shown, so they're not obscured by the community map. Cleared when
    // isExternalFocus flips back to false (next prompt or community pin click).
    if (isExternalFocus) {
      setOverlayVisible(false);
      return;
    }

    // Always hide overlay in hybrid (satellite) view, regardless of zoom
    if (activeMapType === 'hybrid') {
      setOverlayVisible(false);
      return;
    }

    // For roadmap view, determine overlay visibility based on zoom
    if (activeMapType === 'roadmap') {
      // If we have zoom config, use it (using != null to allow zoom level 0)
      if (mapZoomToEnableOverlay != null && activeZoom != null) {
        if (activeZoom >= mapZoomToEnableOverlay && !POIVisible) {
          setOverlayVisible(true);
        } else {
          setOverlayVisible(false);
        }
      } else {
        // No zoom config available - show overlay by default in roadmap
        if (!POIVisible) {
          setOverlayVisible(true);
        }
      }
    }
  }, [
    mapZoomToEnableOverlay,
    activeZoom,
    activeMapType,
    POIVisible,
    isExternalFocus,
    setOverlayVisible,
  ]);

  return overlayView && map && overlayVisible && !isExternalFocus && overlayDOMElem ? ReactDOM.createPortal(<MapOverlay />, overlayDOMElem) : null;
};

export default GoogleMapOverlayViewContainer;